import React from "react";
import {
  NewPredictiveTextField,
  OverlayProvider,
  useViewport,
  NewListItem,
  NewListItemTextSnippet,
  NewSelect,
} from "@bca/ui-library";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "../../components/Button/Button";
import {
  StyledCompanyInformation,
  StyledContent,
  StyledTitle,
  StyledGroup,
  StyledSection,
  StyledInput,
  StyledSubtitle,
  StyledRequired,
  StyledCheckbox,
  StyledCheckboxGroup,
  StyledStateSelect,
} from "./CompanyInformation.styled";
import WebStepper from "../../components/WebStepper/WebStepper";
import {
  getCompanyInformation,
  verifyCompanyDuplicate,
} from "../../reducers/companyInformationSlice";
import {
  changeActivitySector,
  changeCity,
  changeCompanyName,
  changeCompanyId,
  changeLegalForm,
  changeSiren,
  changeSiret,
  changeStreetNameAndNumber,
  changeVendorQuestionCheckbox,
  changeZipCode,
  changeCompanyPhoneNumber,
  changeCompanySiteOrPortal,
  resetCompanyInformation,
  changeCompanyType,
  changeDuns,
} from "../../reducers/formSlice";
import CompanyDuplicateModal from "../../components/CompanyDuplicateModal/CompanyDuplicateModal";
import CircularStepper from "../../components/CircularStepper/CircularStepper";
import fetchMobileStepper from "../../utils/fetchMobileStepper";
import { COMPANY_INFORMATION_PATH, HOME_PATH } from "../../constants/paths";
import { setShowServerErrorMessage } from "../../reducers/errorManagementSlice";
import {
  initializeDigitalData,
  updateInitializedDigitalDataForFirstPage,
} from "../../hooks/scriptsHooks";

const CompanyInformation = () => {
  const { page_content, loading } = useSelector(
    (state) => state.companyInformationScreen
  );

  const { country_name: countrySelected, ...formState } = useSelector(
    (state) => state.form
  );

  const navigate = useNavigate();

  if (!countrySelected) {
    navigate(HOME_PATH, { replace: true });
  }

  const { value: languageCode, country_iso3 } = useSelector(
    (state) => state.languageOptions
  );

  const [showModal, setShowModal] = React.useState(false);

  const [mobileStepper, setMobileStepper] = React.useState();

  const [sirenNumberValid, setSirenNumberValid] = React.useState(true);
  const [siretNumberValid, setSiretNumberValid] = React.useState(true);

  const [continueButtonDisabled, setContinueButtonDisabled] = React.useState(
    page_content?.continue_button?.disable || true
  );

  const dispatch = useDispatch();
  const vp = useViewport();
  const isMobile = vp.lessThan.md;

  React.useEffect(() => {
    window.scrollTo(0, 0);

    dispatch(getCompanyInformation(countrySelected, languageCode));

    const getResult = async () => {
      const result = await fetchMobileStepper(languageCode, setMobileStepper);
      dispatch(setShowServerErrorMessage(result.responseStatus));
    };
    getResult();
  }, [countrySelected, dispatch, languageCode]);

  React.useEffect(() => {
    if (
      (page_content?.company_name?.required === true
        ? formState.company_name.value !== ""
        : true) &&
      (page_content?.activity_sector?.required === true
        ? formState.activity_sector.value !== ""
        : true) &&
      (page_content?.legal_form?.required === true
        ? formState.legal_form.value !== ""
        : true) &&
      (page_content?.siren?.required === true
        ? formState.siren.value !== ""
        : true) &&
      (page_content?.siret?.required === true
        ? formState.siret.value !== ""
        : true) &&
      (page_content?.siren?.required === true && countrySelected === "FR"
        ? sirenNumberValid
        : true) &&
      (page_content?.siret?.required === true && countrySelected === "FR"
        ? siretNumberValid
        : true) &&
      (page_content?.vendor_question_checkbox?.required === true
        ? formState.vendor_question_checkbox.value.some(
            (item) => item.checked === true
          )
        : true)
    ) {
      setContinueButtonDisabled(false);
    } else {
      setContinueButtonDisabled(true);
    }
  }, [
    countrySelected,
    formState.activity_sector,
    formState.company_name,
    formState.legal_form,
    formState.siren,
    formState.siret,
    formState.vendor_question_checkbox.value,
    page_content?.activity_sector?.required,
    page_content?.company_name?.required,
    page_content?.legal_form?.required,
    page_content?.siren?.required,
    page_content?.siret?.required,
    page_content?.vendor_question_checkbox?.required,
    sirenNumberValid,
    siretNumberValid,
  ]);

  const handleVerifyCompanyDuplicate = async (companyName, languageISO) => {
    if (companyName) {
      const result = await verifyCompanyDuplicate({
        companyName,
        languageISO,
      });
      const { data } = result;
      if (data?.account_id) {
        await setShowModal(true);
        dispatch(changeCompanyId(data?.account_id));
        dispatch(changeCompanyName(data?.company_name));
        dispatch(changeStreetNameAndNumber(data?.street));
        dispatch(changeCity(data?.city));
        dispatch(changeZipCode(data?.zipcode));
      }
    }
  };

  React.useEffect(() => {
    if (window.digitalData) {
      updateInitializedDigitalDataForFirstPage();
      window.digitalData.push({
        event: {
          name: "pageview",
        },
        journey: {
          name: "new-registration",
          stepNumber: "1",
          stepName: "step 1 company information",
        },
        pageInfo: {
          pageTitle: "Company Information",
          pagePath: `/${COMPANY_INFORMATION_PATH}`,
          previousPagePath: "/home",
        },
      });
    } else {
      initializeDigitalData();
      updateInitializedDigitalDataForFirstPage();
      window.digitalData.push({
        event: {
          name: "pageview",
        },
        journey: {
          name: "new-registration",
          stepNumber: "1",
          stepName: "step 1 company information",
        },
        pageInfo: {
          pageTitle: "Company Information",
          pagePath: `/${COMPANY_INFORMATION_PATH}`,
          previousPagePath: "/home",
        },
        site: {
          brandCode: country_iso3,
          brandName: "bca europe",
          language: languageCode,
          platformType: "cms",
          country: languageCode,
        },
      });
    }
  }, [country_iso3, languageCode]);

  const handleCloseModal = () => {
    setShowModal(false);
    dispatch(resetCompanyInformation());
  };

  const baseUrl = process.env.REACT_APP_API;

  const [companyNames, setCompanyNames] = React.useState([]);

  const fetchCompanyNames = (companyName) =>
    companyName.length > 1 &&
    fetch(`${baseUrl}/api/search-company/${countrySelected}/${companyName}/`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    }).then(async (res) => {
      const statusCode = res.status;

      dispatch(setShowServerErrorMessage(statusCode));

      if (statusCode === 204) {
        return null;
      }
      const data = await res.json();

      setCompanyNames(data);
      return data;
    });

  const handlePredictiveTextChange = async (event) => {
    const { value } = event.target;

    dispatch(changeCompanyName(value));

    await fetchCompanyNames(value);
  };

  const handleSelectItem = async (selectedValue) => {
    if (selectedValue) {
      // Reset list
      companyNames.forEach((item) => {
        item.selected = false;
      });

      // Find item and update text box state
      const foundItem = companyNames.find(
        (item) => item.duns.toString() === selectedValue
      );
      if (foundItem) {
        handleVerifyCompanyDuplicate(foundItem.name, languageCode);
        dispatch(changeCompanyName(foundItem?.name));
        dispatch(changeDuns(foundItem.duns));
        const result = await fetch(
          `${baseUrl}/api/details-company/${foundItem.duns}/`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          }
        );
        dispatch(setShowServerErrorMessage(result.status));
        const companyData = await result.json();

        if (typeof companyData?.activity_sector === "string")
          dispatch(changeActivitySector(companyData?.activity_sector));

        if (typeof companyData?.legal_form === "string")
          dispatch(changeLegalForm(companyData?.legal_form));

        if (
          typeof companyData?.primary_address?.street_name_and_number ===
          "string"
        )
          dispatch(
            changeStreetNameAndNumber(
              companyData?.primary_address?.street_name_and_number
            )
          );

        if (typeof companyData?.primary_address?.city === "string")
          dispatch(changeCity(companyData?.primary_address?.city));

        if (typeof companyData?.primary_address?.zip_code === "string")
          dispatch(changeZipCode(companyData?.primary_address?.zip_code));

        if (typeof companyData?.primary_phone_number === "string")
          dispatch(changeCompanyPhoneNumber(companyData?.primary_phone_number));

        if (typeof companyData?.website === "string")
          dispatch(changeCompanySiteOrPortal(companyData?.website));

        if (typeof companyData?.website === "string")
          dispatch(changeCompanySiteOrPortal(companyData?.website));
      }

      // Clear filtered results
      setCompanyNames([]);
    }
  };

  const handleBlur = () => {
    handleVerifyCompanyDuplicate(formState.company_name.value, languageCode);
    setCompanyNames([]);
  };

  return (
    <OverlayProvider>
      {showModal && (
        <CompanyDuplicateModal onClose={handleCloseModal} isMobile={isMobile} />
      )}
      <StyledCompanyInformation>
        {!loading && (
          <>
            <StyledContent>
              {!isMobile && (
                <>
                  <WebStepper activeStep={0} />
                  <StyledTitle variant="h1" isMobile={isMobile}>
                    {page_content?.header}
                  </StyledTitle>
                </>
              )}
              {isMobile && (
                <CircularStepper
                  mobileStepper={mobileStepper}
                  activeStepIndex={0}
                />
              )}
              <StyledSection>
                <StyledGroup>
                  <StyledSubtitle variant="h2">
                    {page_content?.vendor_question_checkbox?.label}
                    {page_content?.vendor_question_checkbox?.required && (
                      <StyledRequired>*</StyledRequired>
                    )}
                  </StyledSubtitle>
                  <StyledCheckboxGroup isMobile={isMobile}>
                    {formState.vendor_question_checkbox?.value?.map(
                      (vendorCheckbox) => (
                        <StyledCheckbox
                          data-cy="company-information-vendor-question-checkbox"
                          key={vendorCheckbox.label}
                          label={vendorCheckbox.label}
                          checked={vendorCheckbox.checked}
                          onChange={() =>
                            dispatch(
                              changeVendorQuestionCheckbox(vendorCheckbox.value)
                            )
                          }
                        />
                      )
                    )}
                  </StyledCheckboxGroup>
                </StyledGroup>

                <StyledGroup>
                  <NewPredictiveTextField
                    data-cy="company-information-company-name"
                    state="default"
                    colorOn="bg_white"
                    label={page_content?.company_name?.label}
                    required={page_content?.company_name?.required}
                    showOptions={companyNames.length > 0}
                    onChange={(e) => handlePredictiveTextChange(e)}
                    onSelectItem={handleSelectItem}
                    value={formState.company_name.value}
                    onBlur={(e) => companyNames.length > 0 && handleBlur(e)}
                  >
                    {companyNames.length > 0
                      ? companyNames.map((item) => {
                          return (
                            <NewListItem
                              key={item.duns}
                              value={item.duns?.toString()}
                              withSpace
                            >
                              <NewListItemTextSnippet text={item.name} />
                            </NewListItem>
                          );
                        })
                      : null}
                  </NewPredictiveTextField>
                </StyledGroup>

                <StyledGroup>
                  <StyledInput
                    data-cy="company-information-activity-sector"
                    state="default"
                    colorOn="bg_white"
                    label={page_content?.activity_sector?.label}
                    required={page_content?.activity_sector?.required}
                    value={formState.activity_sector.value}
                    onChange={(e) =>
                      dispatch(changeActivitySector(e.target.value))
                    }
                  />
                </StyledGroup>

                <StyledGroup>
                  <StyledInput
                    data-cy="company-information-legal-form"
                    state="default"
                    colorOn="bg_white"
                    label={page_content?.legal_form?.label}
                    required={page_content?.legal_form?.required}
                    value={formState.legal_form.value}
                    onChange={(e) => dispatch(changeLegalForm(e.target.value))}
                  />
                </StyledGroup>

                {countrySelected === "FR" && (
                  <>
                    <StyledGroup>
                      <StyledInput
                        data-cy="company-information-siren"
                        state={sirenNumberValid ? "default" : "error"}
                        colorOn="bg_white"
                        label={page_content?.siren?.label}
                        required={page_content?.siren?.required}
                        value={formState.siren.value}
                        message={
                          sirenNumberValid
                            ? ""
                            : page_content?.siren_error_message
                        }
                        onChange={(e) => {
                          let regex;
                          dispatch(changeSiren(e.target.value));

                          if (page_content?.siren_validator) {
                            regex = new RegExp(page_content?.siren_validator);
                          }

                          if (e.target.value !== "") {
                            setSirenNumberValid(regex.test(e.target.value));
                          } else {
                            setSirenNumberValid(false);
                          }
                        }}
                        onBlur={(e) => {
                          if (sirenNumberValid) {
                            dispatch(changeSiren(e.target.value));
                          }
                        }}
                      />
                    </StyledGroup>

                    <StyledGroup>
                      <StyledInput
                        data-cy="company-information-siret"
                        state={siretNumberValid ? "default" : "error"}
                        colorOn="bg_white"
                        label={page_content?.siret?.label}
                        required={page_content?.siret?.required}
                        value={formState.siret.value}
                        message={
                          siretNumberValid
                            ? ""
                            : page_content?.siret_error_message
                        }
                        onChange={(e) => {
                          let regex;
                          dispatch(changeSiret(e.target.value));

                          if (page_content?.siret_validator) {
                            regex = new RegExp(page_content?.siret_validator);
                          }

                          if (e.target.value !== "") {
                            setSiretNumberValid(regex.test(e.target.value));
                          } else {
                            setSiretNumberValid(false);
                          }
                        }}
                        onBlur={(e) => {
                          if (siretNumberValid) {
                            dispatch(changeSiret(e.target.value));
                          }
                        }}
                      />
                    </StyledGroup>
                  </>
                )}
                {page_content?.company_type?.options && (
                  <StyledGroup>
                    <StyledStateSelect
                      data-cy="company-details-company-type"
                      onChange={(event) =>
                        dispatch(changeCompanyType(event.target.value))
                      }
                      label={page_content?.company_type?.label}
                      value={formState?.company_type?.value}
                    >
                      {page_content?.company_type?.options?.map((option) => (
                        <NewSelect.Option
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </NewSelect.Option>
                      ))}
                    </StyledStateSelect>
                  </StyledGroup>
                )}
              </StyledSection>
            </StyledContent>
            <Button
              backButtonText={page_content?.back_button?.label}
              backLink="#"
              backButtonDisabled={page_content?.back_button?.disable}
              continueButtonText={page_content?.continue_button?.label}
              nextLink="/company-details"
              nextButtonDisabled={continueButtonDisabled}
              onBackClick={() => navigate(-1)}
              isMobile={vp.lessThan.md}
            />
          </>
        )}
      </StyledCompanyInformation>
    </OverlayProvider>
  );
};

export default CompanyInformation;
